import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FileText, 
  Package, 
  Building2, 
  TrendingUp, 
  Zap, 
  Shield, 
  CheckCircle,
  ArrowRight,
  Sparkles
} from 'lucide-react';
import './LandingPage.css';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="landing-page">
      {/* Navigation */}
      <nav className="landing-nav">
        <div className="nav-container">
          <div className="nav-brand">
            <Sparkles size={28} />
            <span>QR</span>
          </div>
          <div className="nav-actions">
            <button className="btn-secondary" onClick={() => navigate('/login')}>
              Login
            </button>
            <button className="btn-primary" onClick={() => navigate('/signup')}>
              Get Started
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-container">
          <div className="hero-badge">
            <Zap size={16} />
            <span>Fast & Efficient Invoice Management</span>
          </div>
          <h1 className="hero-title">
            Professional Invoice
            <br />
            <span className="gradient-text">Management Made Simple</span>
          </h1>
          <p className="hero-subtitle">
            Create, manage, and track invoices with ease. Perfect for businesses of all sizes.
            Generate professional invoices in seconds with our intuitive platform.
          </p>
          <div className="hero-actions">
            <button className="btn-hero-primary" onClick={() => navigate('/signup')}>
              Get Started Free
              <ArrowRight size={20} />
            </button>
            <button className="btn-hero-secondary" onClick={() => navigate('/login')}>
              Sign In
            </button>
          </div>
          <div className="hero-stats">
            <div className="stat-item">
              <h3>10K+</h3>
              <p>Invoices Generated</p>
            </div>
            <div className="stat-item">
              <h3>500+</h3>
              <p>Happy Businesses</p>
            </div>
            <div className="stat-item">
              <h3>99.9%</h3>
              <p>Uptime</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="features-container">
          <div className="section-header">
            <h2>Everything You Need</h2>
            <p>Powerful features to streamline your invoice management</p>
          </div>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">
                <FileText size={32} />
              </div>
              <h3>Easy Invoice Creation</h3>
              <p>Generate professional invoices in seconds with our intuitive interface and customizable templates</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <Package size={32} />
              </div>
              <h3>Product Management</h3>
              <p>Organize and manage your product catalog with SKU tracking, pricing, and inventory details</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <Building2 size={32} />
              </div>
              <h3>Multi-Company Support</h3>
              <p>Manage multiple businesses from a single dashboard with separate company profiles</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <TrendingUp size={32} />
              </div>
              <h3>Analytics & Insights</h3>
              <p>Track your business performance with real-time analytics and detailed reports</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <Zap size={32} />
              </div>
              <h3>Lightning Fast</h3>
              <p>Generate bulk invoices instantly with our optimized PDF generation engine</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <Shield size={32} />
              </div>
              <h3>Secure & Reliable</h3>
              <p>Your data is protected with enterprise-grade security and automatic backups</p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section">
        <div className="benefits-container">
          <div className="benefits-content">
            <h2>Why Choose QR?</h2>
            <p className="benefits-subtitle">
              Join hundreds of businesses that trust us with their invoice management
            </p>
            <ul className="benefits-list">
              <li>
                <CheckCircle size={24} />
                <div>
                  <h4>Save Time</h4>
                  <p>Automate invoice generation and reduce manual work by 80%</p>
                </div>
              </li>
              <li>
                <CheckCircle size={24} />
                <div>
                  <h4>Professional Output</h4>
                  <p>Create polished, branded invoices that impress your clients</p>
                </div>
              </li>
              <li>
                <CheckCircle size={24} />
                <div>
                  <h4>Easy to Use</h4>
                  <p>Intuitive interface that requires no training or technical knowledge</p>
                </div>
              </li>
              <li>
                <CheckCircle size={24} />
                <div>
                  <h4>Always Accessible</h4>
                  <p>Access your invoices anytime, anywhere from any device</p>
                </div>
              </li>
            </ul>
          </div>
          <div className="benefits-visual">
            <div className="visual-card">
              <div className="visual-header">
                <div className="visual-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
              <div className="visual-content">
                <div className="visual-stat">
                  <FileText size={40} />
                  <h3>1,234</h3>
                  <p>Total Invoices</p>
                </div>
                <div className="visual-progress">
                  <div className="progress-bar">
                    <div className="progress-fill" style={{ width: '75%' }}></div>
                  </div>
                  <p>75% Paid</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-container">
          <h2>Ready to Get Started?</h2>
          <p>Join thousands of businesses using QR for their invoice management</p>
          <button className="btn-cta" onClick={() => navigate('/signup')}>
            Start Free Today
            <ArrowRight size={20} />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="footer-container">
          <div className="footer-brand">
            <Sparkles size={24} />
            <span>QR</span>
          </div>
          <p>© 2025 QR. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
